export const TUTORIALS = {
  planner: {
    title: "Planner Guide",
    chapters: [
      {
        id: "p1",
        title: "Dashboard Overview",
        text: "Welcome to the Planner Dashboard. This is your command center for all logistics operations. You can see all construction sites, their machines, active requests, and the live activity feed. Use the tabs on the left to navigate between sites.",
        trigger: "dashboard-load",
      },
      {
        id: "p2",
        title: "Importing Data",
        text: "Click the upload icon in the top bar to open the Philogic Data Import panel. You can upload PDF files, CSV spreadsheets, or photos of site documents. The AI will automatically extract sites, machines, personnel, and fuel data. Review the results and click Confirm to add them to your hub.",
        trigger: "import-data-btn",
      },
      {
        id: "p3",
        title: "Managing Requests",
        text: "Each site shows incoming requests from machinists. Fuel requests can be approved and assigned to drivers. Transport requests require your confirmation before the machine is moved. Repair and maintenance requests are forwarded to the Service portal. Click on a site to expand its detail view.",
        trigger: "site-row",
      },
      {
        id: "p4",
        title: "Transport Confirmation",
        text: "When a machinist requests a machine transport to another site, you will see a blue Confirm button. Clicking this moves the machine to the destination site in the hub data. The transport portal and driver are notified automatically.",
        trigger: "confirm-transport",
      },
      {
        id: "p5",
        title: "PDF Reports",
        text: "Hover over the document icon in the header to export PDF logs. You can generate reports for Sites, Machines, or Drivers. These contain all current data including GPS coordinates, status, and activity.",
        trigger: "pdf-export-btn",
      },
      {
        id: "p6",
        title: "Live Feed & Feedback",
        text: "The live feed on the right shows all hub activity in real time. Driver feedback appears color-coded: green for successful deliveries, red for incidents like access denied or obstacles. This helps you monitor site accessibility.",
        trigger: "live-feed",
      },
      {
        id: "p7",
        title: "Calendar & Leave Management",
        text: "Click the calendar icon to open the hub calendar. Create appointments, schedule training sessions, upload toolbox documents (PDF), and manage driver leave requests. Pending leave requests show a red notification badge. Approve or reject leave from the calendar panel.",
        trigger: "calendar-btn",
      },
      {
        id: "p8",
        title: "Route Planning",
        text: "Click the route icon to open the Route Builder. Select sites from the list, choose a driver, and assign the route. The driver will see the assigned sites with fuel status highlights. Reuse routes from history to save time.",
        trigger: "route-builder-btn",
      },
      {
        id: "p9",
        title: "Machine Bulk Import",
        text: "Inside each site detail, click '+Set Machines' to add equipment. You can add machines manually, upload a PDF/CSV/photo of a machine list, or bulk paste copied text. Philogic AI will decipher and create all machines automatically.",
        trigger: "add-machine-btn",
      },
      {
        id: "p10",
        title: "Fuel Highlights & Site Pool",
        text: "Sites and machines with active fuel requests glow RED. After refueling, they turn GREEN for 12 hours, then fade. Delete sites and machines when projects finish. Each subject has a PDF log button for activity export.",
        trigger: "site-row",
      },
    ],
  },
  driver: {
    title: "Driver Guide",
    chapters: [
      {
        id: "d1",
        title: "Starting Your Shift",
        text: "Toggle the power button to start your day. Complete the pre-departure checklist before accepting deliveries. All items must be checked before the Deliver button becomes active.",
        trigger: "toggle-shift-btn",
      },
      {
        id: "d2",
        title: "Delivery Process",
        text: "Your assigned sites and requests appear in the planning section. Each request shows the machine number, type, and urgency. Click Deliver to open the delivery confirmation where you enter the diesel and AdBlue amounts.",
        trigger: "deliver",
      },
      {
        id: "d3",
        title: "Delivery Feedback",
        text: "After confirming a delivery, a feedback modal appears. Choose All Good for successful deliveries, or Problem to report an incident. Pre-made incident buttons include: Access Denied, Danger Route, Obstacles, Mud, and Machine Unaccessible. Your feedback is visible to the planner and machinist.",
        trigger: "feedback-modal",
      },
      {
        id: "d4",
        title: "Machine Status",
        text: "Each site card shows machine status badges. Red means DEFECT, blue means TRANSPORT in progress, and purple means UPKEEP or maintenance. These statuses are set by the machinist and updated in real time.",
        trigger: "driver-machines",
      },
      {
        id: "d5",
        title: "Messages & High Fives",
        text: "Click the message icon to view messages from the planner and give high fives. The notification badge shows unread message count. High fives are a way to recognize good teamwork.",
        trigger: "driver-messages-btn",
      },
      {
        id: "d6",
        title: "Fuel Highlights",
        text: "Machines and sites that need fuel glow RED in your dashboard. After you refuel them, they turn GREEN for 12 hours. This helps you quickly spot which machines are waiting.",
        trigger: "driver-site",
      },
      {
        id: "d7",
        title: "Assigned Routes",
        text: "Your planner can assign you a route: a list of sites to visit. The route appears at the top of your dashboard. Each site shows its fuel status. Complete all sites to finish your route.",
        trigger: "assigned-route",
      },
      {
        id: "d8",
        title: "Calendar & Leave",
        text: "Click the calendar icon to view appointments, training sessions, and request leave. Your leave request goes to the planner for approval. You'll see the status update (Approved/Rejected) in real time.",
        trigger: "driver-calendar-btn",
      },
    ],
  },
  machinist: {
    title: "Machinist Guide",
    chapters: [
      {
        id: "m1",
        title: "Selecting Your Site",
        text: "Choose your construction site from the site selection screen. Your GPS location is auto-detected to help identify the nearest site.",
        trigger: "site-selection",
      },
      {
        id: "m2",
        title: "Creating Requests",
        text: "Each machine card has quick action buttons. The green button creates a fuel request, red creates a repair request, and blue creates a transport request. You can also tap the machine icon for a full request modal with all four types: Fuel, Repair, Maintenance, and Transport.",
        trigger: "fuel-btn",
      },
      {
        id: "m3",
        title: "Site Info Editing",
        text: "Click the Edit button on the site info card to update the access code and access route. This information is shared with drivers and the planner to improve site accessibility.",
        trigger: "edit-site-btn",
      },
      {
        id: "m4",
        title: "Machine Status",
        text: "Use the gear icon on each machine to change its status. Options include: In Use (normal), Defect (needs repair), Maintenance (scheduled service), and Transport Needed. Status changes propagate to the planner, service, and transport portals.",
        trigger: "machine-status",
      },
      {
        id: "m5",
        title: "Registering Rental Equipment",
        text: "Click the purple Rental button to register external or rented machinery on your site. Enter the machine type and ID. Registered rentals appear with a purple border in your machine pool.",
        trigger: "add-rental-btn",
      },
      {
        id: "m6",
        title: "Setting Site Phase",
        text: "In the site editing panel, you can now set the site phase/stage: Groundwork, Foundation, Structure, Roofing, Finishing, Landscaping, or Demolition. This phase is visible to the planner and driver, helping coordinate activities. Phase changes are logged in the activity feed.",
        trigger: "site-phase-select",
      },
    ],
  },
  service: {
    title: "Service Guide",
    chapters: [
      {
        id: "s1",
        title: "Service Portal Overview",
        text: "The Service Portal receives all repair and maintenance requests from machinists. The dashboard shows counts of active repairs, maintenance tasks, and resolved issues. Requests are sorted by urgency.",
        trigger: "dashboard-load",
      },
      {
        id: "s2",
        title: "Handling Requests",
        text: "Click on a request to expand its details. You can see the machine information, GPS location, site details, and the machinist's notes. Use the Maps link to navigate to the exact location.",
        trigger: "service-req",
      },
      {
        id: "s3",
        title: "Resolving Issues",
        text: "Add resolution notes describing what was done, then click Mark Resolved. This updates the machine status and notifies the planner through the live feed.",
        trigger: "resolve-btn",
      },
    ],
  },
  transport: {
    title: "Transport Guide",
    chapters: [
      {
        id: "t1",
        title: "Transport Portal Overview",
        text: "The Transport Portal shows all machine relocation requests. The dashboard displays pending transports, machines currently in transit, and completed relocations. The map view shows all site locations and transport routes.",
        trigger: "dashboard-load",
      },
      {
        id: "t2",
        title: "Transport Routes",
        text: "Each transport request shows the origin and destination sites with GPS links. Click on a request to see the full route visualization including machine details and the Google Maps links for both locations.",
        trigger: "transport-req",
      },
      {
        id: "t3",
        title: "Confirming Transport",
        text: "Click Confirm Transport to complete the relocation. This moves the machine to the destination site in the hub data, updates its status back to Normal, and notifies the planner and machinist.",
        trigger: "confirm-transport",
      },
    ],
  },
  viewer: {
    title: "Viewer Guide",
    chapters: [
      {
        id: "v1",
        title: "Viewer Dashboard",
        text: "The Viewer dashboard provides a read-only overview of all hub activity. You can see the live feed, site status, and machine information without being able to make changes.",
        trigger: "dashboard-load",
      },
    ],
  },
};
